import { useEffect, useState } from 'react'
import { api, type ZefixTreffer } from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'

/**
 * Firmensuche im Handelsregister, eingebaut in die Kundenanlage.
 *
 * Was sie tut: ein Name rein, eine kurze Trefferliste raus, ein Klick füllt
 * Name, Adresse und UID ins Formular darunter. Was sie nicht tut: irgendetwas
 * speichern. Der Kunde entsteht erst, wenn jemand «Anlegen» drückt – und dann
 * mit den Werten, die dann im Formular stehen, nicht mit denen aus dem
 * Register. Wer die vorgeschlagene Adresse korrigiert, behält seine Korrektur.
 *
 * Warum das wichtig ist: das Handelsregister führt den Sitz einer Firma, und
 * der ist nicht immer die Rechnungsadresse. Ein Übernehmen, das sich nicht
 * mehr ändern liesse, wäre bequemer und im Alltag falsch.
 *
 * Ist die Suche noch nicht eingerichtet, steht hier eine Zeile und dahinter
 * eine Erklärung, die sich öffnen lässt. Kein ausgegrauter Knopf: der erklärt
 * nichts und ärgert bei jedem Anlegen aufs Neue. Und auch kein Verstecken,
 * denn Raporto ist eine Cloud mit vielen Mandanten, und jeder meldet seinen
 * Zefix-Zugang selbst an – wer nichts sieht, weiss nicht, dass es etwas
 * einzurichten gäbe.
 */

export function Handelsregister({
  aufUebernehmen,
  aufEinstellungen
}: {
  aufUebernehmen: (werte: {
    name: string
    street: string
    zip: string
    city: string
    uid: string
  }) => void
  aufEinstellungen?: () => void
}): JSX.Element | null {
  const [eingerichtet, setEingerichtet] = useState<boolean | null>(null)
  const [darfEinrichten, setDarfEinrichten] = useState(false)
  const [anleitungOffen, setAnleitungOffen] = useState(false)
  const [suche, setSuche] = useState('')
  const [treffer, setTreffer] = useState<ZefixTreffer[] | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [holt, setHolt] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let weg = false
    void api
      .zefixStatus()
      .then((a) => {
        if (weg) return
        setEingerichtet(a.eingerichtet)
        setDarfEinrichten(a.darfEinrichten)
      })
      /*
        Ein Fehler beim Nachfragen heisst hier: nicht verfügbar. Nicht mehr.
        Er darf keine Meldung auslösen – niemand hat nach der Suche gefragt,
        der Bildschirm hat von sich aus nachgeschaut.
      */
      .catch(() => {
        if (!weg) setEingerichtet(false)
      })
    return () => {
      weg = true
    }
  }, [])

  // Solange die Antwort aussteht, steht hier nichts. Ein Kasten, der eine
  // halbe Sekunde später durch einen anderen ersetzt wird, ist Unruhe.
  if (eingerichtet === null) return null

  /*
    Nicht eingerichtet heisst: erklären, nicht verstecken.

    Die erste Fassung zeigte in diesem Fall gar nichts. Das ist für einen
    Betrieb richtig, der von Zefix nie gehört hat – und falsch für alle
    anderen, denn Raporto ist eine Cloud mit vielen Mandanten, und jeder
    meldet seinen Zugang selbst an. Wer nichts sieht, kann nicht wissen, dass
    es etwas gibt, das er einrichten könnte.

    Ein Hinweis und kein ausgegrauter Knopf: ein Knopf, der nicht geht,
    erklärt nichts und ärgert bei jedem Anlegen aufs Neue.
  */
  if (!eingerichtet) {
    return (
      <div className="o-hreg o-hreg-aus">
        <div className="o-hreg-aus-zeile">
          <span className="o-hreg-aus-text">
            Firmen aus dem Handelsregister übernehmen – noch nicht eingerichtet.
          </span>
          <button
            type="button"
            id="btn-hreg-anleitung"
            className="o-btn-ghost"
            onClick={() => setAnleitungOffen(true)}
          >
            Was ist das?
          </button>
        </div>
        {anleitungOffen && (
          <Anleitung
            darfEinrichten={darfEinrichten}
            aufSchliessen={() => setAnleitungOffen(false)}
            aufEinstellungen={
              aufEinstellungen
                ? () => {
                    setAnleitungOffen(false)
                    aufEinstellungen()
                  }
                : undefined
            }
          />
        )}
      </div>
    )
  }

  async function suchen(): Promise<void> {
    const q = suche.trim()
    if (q.length < 3) {
      setFehler('Mindestens drei Zeichen.')
      return
    }
    setLaeuft(true)
    setFehler(null)
    try {
      const a = await api.zefixSuche(q)
      setTreffer(a.treffer)
    } catch (e) {
      setFehler(fehlerText(e))
      setTreffer(null)
    } finally {
      setLaeuft(false)
    }
  }

  /**
   * Einen Treffer übernehmen.
   *
   * Der zweite Aufruf holt die Adresse, die in der Trefferliste noch nicht
   * steht. Schlägt er fehl, wird trotzdem übernommen, was schon bekannt ist –
   * Name und UID sind mehr als nichts, und die Adresse tippt sich schneller
   * als der Firmenname mit Rechtsform.
   */
  async function uebernehmen(t: ZefixTreffer): Promise<void> {
    setHolt(t.uid)
    try {
      const a = await api.zefixFirma(t.uid)
      const f = a.firma
      aufUebernehmen({
        name: f?.name || t.name,
        street: f?.street ?? '',
        zip: f?.zip ?? '',
        city: f?.city ?? '',
        uid: f?.uid || t.uid
      })
    } catch {
      aufUebernehmen({ name: t.name, street: '', zip: '', city: '', uid: t.uid })
    } finally {
      setHolt(null)
      setTreffer(null)
      setSuche('')
    }
  }

  return (
    <div className="o-hreg">
      <div className="o-field">
        <label htmlFor="hreg-suche">Im Handelsregister suchen</label>
        <div className="o-hreg-zeile">
          <input
            id="hreg-suche"
            value={suche}
            autoComplete="off"
            placeholder="Firmenname"
            onChange={(e) => setSuche(e.target.value)}
            onKeyDown={(e) => {
              /*
                Enter sucht und schickt nicht das Formular ab. Ohne diese Zeile
                legt ein Druck auf Enter im Suchfeld einen Kunden mit leerem
                Namen an – der klassische Weg, wie ein Datensatz «Unbenannt»
                in einen Stamm gerät.
              */
              if (e.key === 'Enter') {
                e.preventDefault()
                void suchen()
              }
            }}
          />
          <button
            type="button"
            id="btn-hreg-suchen"
            className="o-btn"
            disabled={laeuft}
            onClick={() => void suchen()}
          >
            {laeuft ? 'Sucht …' : 'Suchen'}
          </button>
        </div>
        <span className="hint">
          Übernimmt Name, Adresse und UID. Ändern lässt sich danach alles.
        </span>
      </div>

      {fehler && <p className="o-warn-text">{fehler}</p>}

      {treffer !== null && treffer.length === 0 && (
        <p className="o-soft" style={{ marginTop: 8 }}>
          Keine Firma unter diesem Namen. Der Kunde lässt sich von Hand erfassen.
        </p>
      )}

      {treffer !== null && treffer.length > 0 && (
        <ul className="o-hreg-liste">
          {treffer.map((t) => (
            <li key={t.uid}>
              <button type="button" disabled={holt !== null} onClick={() => void uebernehmen(t)}>
                <span className="o-hreg-name">
                  {t.name}
                  {!t.aktiv && <span className="o-chip">gelöscht</span>}
                </span>
                <span className="o-hreg-sub">
                  {[t.rechtsform, t.sitz, t.uid].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Die Anleitung – was Zefix ist, und was der Betrieb tun muss.
 *
 * Ein eigenes Fenster und kein Absatz im Formular, weil es hier um etwas
 * anderes geht als um die Rechnung, die gerade geschrieben wird. Wer es
 * öffnet, hat sich dafür entschieden; wer es nicht öffnet, sieht eine Zeile.
 *
 * Der Inhalt ist bewusst knapp und sagt vier Dinge: was es bringt, dass der
 * Zugang dem Betrieb gehört und nicht uns, wo er ihn bekommt, und wo er ihn
 * einträgt. Alles andere – Preise, Bedingungen, Fristen – steht beim Bund und
 * ändert sich dort, ohne dass wir es mitbekämen. Was wir hier behaupten
 * würden, wäre irgendwann falsch.
 */
function Anleitung({
  darfEinrichten,
  aufSchliessen,
  aufEinstellungen
}: {
  darfEinrichten: boolean
  aufSchliessen: () => void
  aufEinstellungen?: () => void
}): JSX.Element {
  return (
    <div className="o-dialog-grund" role="presentation" onClick={aufSchliessen}>
      <div
        className="o-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hreg-anleitung-titel"
        /* Ein Klick im Fenster darf es nicht schliessen – nur einer daneben. */
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="hreg-anleitung-titel">Firmen aus dem Handelsregister übernehmen</h2>

        <p>
          Der Bund führt im Zentralen Firmenindex (Zefix) jede eingetragene Schweizer Firma mit
          Name, Rechtsform, Sitz, Adresse und UID. Ist der Zugang hinterlegt, tippst du beim Anlegen
          eines Kunden den Firmennamen und übernimmst den Rest mit einem Klick – statt ihn von einem
          Briefkopf abzuschreiben.
        </p>

        <p>
          <strong>Den Zugang meldet jeder Betrieb selbst an.</strong> Raporto benutzt dafür keine
          gemeinsame Kennung: der Bund vergibt sie pro Firma, und eine geteilte Kennung wäre ein
          Verstoss gegen seine Nutzungsbedingungen. Deine Kennung gehört dir und wird von keinem
          anderen Mandanten mitbenutzt.
        </p>

        <ol className="o-dialog-schritte">
          <li>
            Zugang beim Bund beantragen – auf <span className="o-mono">zefix.admin.ch</span> unter
            «Zefix Public REST API». Der Bund entscheidet über Bedingungen und Kosten; wir kennen
            sie nicht und sagen deshalb nichts dazu.
          </li>
          <li>Du bekommst einen Benutzernamen und ein Zugangswort.</li>
          <li>
            Beides in Raporto unter <strong>Einstellungen → Handelsregister</strong> eintragen.
            Danach steht das Suchfeld in jeder Kundenanlage.
          </li>
        </ol>

        <p className="o-soft">
          Das Zugangswort wird verschlüsselt gespeichert, nur auf dem Server benutzt und nie wieder
          angezeigt – auch dir nicht. Ändern heisst neu eintippen.
        </p>

        <div className="o-dialog-fuss">
          {darfEinrichten && aufEinstellungen && (
            <button type="button" className="o-btn o-btn-primary" onClick={aufEinstellungen}>
              Zu den Einstellungen
            </button>
          )}
          {!darfEinrichten && (
            <span className="o-soft">
              Eintragen kann das nur, wer die Einstellungen verwalten darf.
            </span>
          )}
          <button type="button" className="o-btn" onClick={aufSchliessen}>
            Schliessen
          </button>
        </div>
      </div>
    </div>
  )
}
